export class Header {
  private _lastScrollY: number = 0;
  private _showFixedHeader: boolean = false;
  private _defaultMenuActive: boolean = true;
  private _wasShown: boolean = false;

  constructor() {
    this.enableMobileNavigation();
    this.addListeners();
    this.observeMainMenu();
  }

  addListeners() {
    if (this.headerElement) {
      // Now track the scroll position to determine if the user is scrolling up or down
      window.addEventListener('scroll', this.scrollHandler.bind(this));
    }
  }

  observeMainMenu() {
    // add an intersection observer to the default menu to hide the fixed menu when the default menu is visible
    if (this.defaultMenuElement) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const func = entry.isIntersecting ? 'remove' : 'add';
            document.body.classList[func]('has-fixed-menu-transitions');
          });
        }
      );

      observer.observe(this.defaultMenuElement);
    }
  }

  scrollHandler() {
    const currentScrollY = window.scrollY;

    // If we are at the top of the page, it's a full reset
    if (currentScrollY <= 0) {
      if (this._showFixedHeader) {
        this._showFixedHeader = false;
        this._wasShown = false;
        this.showDefaultNav(true);
        this.updateDisplay();
      }

      return;
    }

    // Check if we are within the threshold to show the default menu
    if (currentScrollY <= this.scrollThreshold) {
      if (!this._defaultMenuActive) {
        this.showDefaultNav(true);
      }
    }

    // If the viewer is scrolling down, hide the header
    if (currentScrollY > this._lastScrollY) {
      if (this._showFixedHeader) {
        this._showFixedHeader = false;
        this.updateDisplay();
      }
    } else {
      // If the viewer is scrolling up, show the header
      if (currentScrollY > this.scrollThreshold) {
        if (!this._showFixedHeader) {
          this._showFixedHeader = true;
          if (this._defaultMenuActive) {
            this.showDefaultNav(false);
          }
          this._wasShown = true; // This doesn't reset until we reach the top again
          this.updateDisplay();
        }
      }
    }

    // If the view is scrolling up, show the header


    // Now update the last scroll position
    this._lastScrollY = currentScrollY;
  }

  updateDisplay() {
    const func = this._showFixedHeader ? 'add' : 'remove';
    document.body.classList[func]('show-nav');
  }

  showDefaultNav(state: boolean) {
    const mainMenu = document.querySelector('#main-menu');
    const originalHeader = document.querySelector('.header') as HTMLElement;

    // Set aria-hidden to true
    if (mainMenu) {
      mainMenu.setAttribute('aria-hidden', state ? 'false' : 'true');
      originalHeader.classList[state ? 'remove' : 'add']('lg:invisible');
    }

    const fixedMenu = document.querySelector('#fixed-menu');
    if (fixedMenu) {
      fixedMenu.setAttribute('aria-hidden', state ? 'true' : 'false');
    }

    this._defaultMenuActive = state;
  }

  get translateY(): number {
    return Math.max(-window.scrollY, -this.headerHeight);
  }

  toggleNav() {
    if (!this._showFixedHeader) {
      document.body.classList.add('show-nav', 'show-x');
      setTimeout(() => {
        document.body.classList.add('fade-in');
      }, 10);
      this._showFixedHeader = true;
    } else {
      document.body.classList.remove('fade-in', 'show-x');
      setTimeout(() => {
        document.body.classList.remove('show-nav');
      }, 500);
      this._showFixedHeader = false;
    }
  }

  enableMobileNavigation() {
    const navTrigger = document.querySelector('#nav-trigger');

    if (navTrigger) {
      navTrigger.addEventListener('click', this.toggleNav.bind(this));
    }
  }

  get headerHeight(): number {
    return this.headerElement.clientHeight;
  }

  get scrollThreshold(): number {
    return this.headerHeight * 3;
  }

  get headerElement(): HTMLElement {
    return document.querySelector('.header') as HTMLElement;
  }

  get defaultMenuElement(): HTMLElement | null {
    return document.querySelector('#main-menu');
  }
}
